import React, { useEffect, useState } from "react";
import { Grid, Button, CircularProgress, Stack, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const RateHistoryReport = ({ customers,parties,pstyles, items }) => {
  const [loadingReport, setLoadingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Local state for filtered lists
  const [filteredPStyles, setFilteredPStyles] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);

  const [filters, setFilters] = useState({
    party: "",
    p_style: "",
    item: "",
  });
  useEffect(() => {
    const fetchPStyles = async () => {
      if (!filters.party) {
        setFilteredPStyles([]);
        setFilters(prev => ({ ...prev, p_style: "", item: "" }));
        return;
      }
      try {
        // Send party ID as param to get specific production styles
        const res = await api.get(`inventory/pstyles_party?party=${filters.party}`);
        setFilteredPStyles(res.data.message || []);
        // Reset dependent fields
        setFilters(prev => ({ ...prev, p_style: "", item: "" }));
      } catch (error) {
        console.error("Error fetching pstyles:", error);
      }
    };
    fetchPStyles();
  }, [filters.party]);
  useEffect(() => {
    const fetchItems = async () => {
      // If party isn't selected, we can't fetch specific items
      if (!filters.party) {
        setFilteredItems([]);
        return;
      }
      try {
        let url = `inventory/items_party?party=${filters.party}`;
        if (filters.p_style) {
          url += `&p_style=${filters.p_style}`;
        }
        const res = await api.get(url);
        setFilteredItems(res.data.message || []);
        setFilters(prev => ({ ...prev, item: "" }));
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };
    fetchItems();
  }, [filters.party, filters.p_style]);
  const transformRateData = (rateData) => {
    return rateData.map(rate => {
      // Get the latest history entry
      const latestHistory = rate.history[0];

      return {
        date: dayjs(latestHistory.updated_date).format("YYYY-MM-DD"),
        customer: rate.customer?.name || "",
        party: rate.party?.name || "",
        p_style: rate.p_style?.name || "",
        item: rate.item?.name || "",
        previousRate: latestHistory.previous_price,
        newRate: rate.price,
        changedBy: latestHistory.updated_by?.name || ""
      };
    });
  };

  const generateReport = async (exportType = 'html') => {
    const params = new URLSearchParams();

    if (filters.party) {
      params.append('party', filters.party);
    }

    if (filters.p_style) {
      params.append('p_style', filters.p_style);
    }

    if (filters.style) {
      params.append('style', filters.style);
    }

    if (filters.item) {
      params.append('item', filters.item);
    }

    const queryString = params.toString();

    setLoadingReport(true);
    try {
      const response = await api.get(
        `/reports/rate-history-report${queryString ? `?${queryString}` : ''}`
      );

      if (response.data) {
        const selectedParty = filters.party ? parties.find(c => c._id === filters.party)?.name : "All";
        const selectedPstyle = filters.p_style ? pstyles.find(c => c._id === filters.p_style)?.name : "All";
        const selectedItem = filters.item ? items.find(i => i._id === filters.item)?.name : "All";
        const transformedData = transformRateData(response.data.data);

        if (exportType === 'excel') {
          const excelData = transformedData.map(rate => ({
            "Date": rate.date,
            "Customer": rate.customer,
            "Party": rate.party,
            "Production Style": rate.p_style,
            "Style": rate.style,
            "Item": rate.item,
            "Previous Rate": rate.previousRate,
            "New Rate": rate.newRate,
            "Changed By": rate.changedBy
          }));

          exportToExcel(excelData, `Rate_History_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }

        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Rate History Report</title>
            <style>
             body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin: 15px; font-size: 14px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Rate History Report</h1>
              <div class="report-filters">
                <p><strong>Party:</strong> ${selectedParty}</p>
                <p><strong>PStyle/Style:</strong> ${selectedPstyle}</p>
                <p><strong>Item:</strong> ${selectedItem}</p>
                <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
              </div>
              
            </div>
            
            <table>
              <thead>
                <tr>
                  
                  <th>Party</th>
                  <th>Pstyle/Style</th>
                  
                  <th>Item</th>
                  <th>Previous Rate</th>
                  <th>New Rate</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
        `;
        // <table>
        // <thead>
        //   <tr>
        //     <th>Date</th>
        //     <th>Customer</th>
        //     <th>Party</th>
        //     <th>Production Style</th>
        //     <th>Style</th>
        //     <th>Item</th>
        //     <th>Previous Rate</th>
        //     <th>New Rate</th>
        //     <th>Changed By</th>
        //   </tr>
        // </thead>
        // <tbody>
        transformedData.forEach(rate => {
          htmlContent += `
            <tr>
              
              
              <td>${rate.party}</td>
              <td>${rate.p_style}</td>
              
              <td>${rate.item}</td>
              <td>${rate.previousRate}</td>
              <td>${rate.newRate}</td>
              <td>${rate.date}</td>
            </tr>
          `;
        });

        htmlContent += `
              </tbody>
            </table>
            
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;

        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Rate_History_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }

        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingReport(false);
          return;
        }

        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }

        reportWindow.document.write(htmlContent);
        reportWindow.document.close();

        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating rate history report:", error);
      toast.error("Failed to generate rate history report");
    } finally {
      setLoadingReport(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (filters.party) params.append('party', filters.party);
      if (filters.p_style) params.append('p_style', filters.p_style);
      if (filters.style) params.append('style', filters.style);
      if (filters.item) params.append('item', filters.item);

      const queryString = params.toString();

      const response = await api.get(`/reports/rate-history-report${queryString ? `?${queryString}` : ''}`);

      if (response.data) {
        const selectedParty = filters.parties ? parties?.find(c => c._id === filters.party)?.name : "All";
        const selectedPstyle = filters.p_style ? pstyles.find(i => i._id === filters.p_style)?.name : "All";
        
        const selectedItem = filters.item ? items.find(i => i._id === filters.item)?.name : "All";

        const transformedData = transformRateData(response.data.data);

        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Rate History Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Rate History Report</h1>
              <div class="report-filters">
                <p><strong>Party:</strong> ${selectedParty}</p>
                <p><strong>PStyle/Style:</strong> ${selectedPstyle}</p>
                <p><strong>Item:</strong> ${selectedItem}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  
                 
                  <th>Party</th>
                  <th>Pstyle/Style</th>
                 
                  <th>Item</th>
                  <th>Previous Rate</th>
                  <th>New Rate</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
        `;

        transformedData.forEach(rate => {
          htmlContent += `
            <tr>
              
              
              <td>${rate.party}</td>
              <td>${rate.p_style}</td>
              
              <td>${rate.item}</td>
              <td>${rate.previousRate}</td>
              <td>${rate.newRate}</td>
              <td>${rate.date}</td>
            </tr>
          `;
        });

        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;

        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);

        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Rate_History_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={2.5}>
        <FormControl fullWidth>
          <InputLabel>Party</InputLabel>
          <Select
            value={filters.party}
            label="Party"
            onChange={(e) => setFilters({ ...filters, party: e.target.value })}
          >
            <MenuItem value="">All Parties</MenuItem>
            {parties?.map((customer) => (
              <MenuItem key={customer._id} value={customer._id}>
                {customer.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={2.5}>
        <FormControl fullWidth disabled={!filters.party}>
          <InputLabel>Production Style</InputLabel>
          <Select
            value={filters.p_style}
            label="Production Style"
            onChange={(e) => setFilters({ ...filters, p_style: e.target.value })}
          >
            <MenuItem value="">All PStyles/Styles</MenuItem>
            {filteredPStyles.map((ps) => (
              <MenuItem key={ps._id} value={ps._id}>{ps.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
     
      <Grid item xs={24} sm={5.0}>
        <FormControl fullWidth disabled={!filters.party}>
          <InputLabel>Item</InputLabel>
          <Select
            value={filters.item}
            label="Item"
            onChange={(e) => setFilters({ ...filters, item: e.target.value })}
          >
            <MenuItem value="">All Items</MenuItem>
            {filteredItems.map((item) => (
              <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateReport('html')}
            disabled={loadingReport || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{
              height: "38px",
              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            {loadingReport ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('pdf')}
            disabled={loadingReport || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('excel')}
            disabled={loadingReport || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('email')}
            disabled={loadingReport || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default RateHistoryReport;
